import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon } from 'lucide-react';

import { cn } from 'shared/lib/utils';

const progressStepVariants = cva('h-1 w-full min-w-2', {
  variants: {
    state: {
      pending:
        'bg-gray00 shadow-[0_4px_4px_var(--color-white),inset_0_2px_1px_rgb(0_0_0/0.04)]',
      current: 'bg-accent30',
      complete: 'bg-accent',
      celebrating: 'relative',
    },
  },
  defaultVariants: {
    state: 'pending',
  },
});

type ProgressStepState = NonNullable<
  VariantProps<typeof progressStepVariants>['state']
>;

type ProgressStepsProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  totalSteps: number;
  currentStep: number;
  celebrateLastStep?: boolean;
};

function ProgressStep({
  state,
  children = null,
}: {
  state: ProgressStepState;
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot="progress-step"
      data-state={state}
      className={progressStepVariants({ state })}
    >
      {children}
    </div>
  );
}

function CelebratingStep() {
  const [ended, setEnded] = React.useState(false);
  if (ended) return <ProgressStep state="complete" />;

  return (
    <ProgressStep state="celebrating">
      <div className="absolute inset-0 animate-progress-step-squeeze motion-reduce:[animation-duration:1ms]" />
      <div
        data-slot="progress-step-celebration"
        className="absolute inset-0 flex items-center justify-center"
      >
        <div
          className="flex size-7 animate-progress-step-pop items-center justify-center rounded-full bg-accent text-white motion-reduce:[animation-duration:1ms]"
          onAnimationEnd={(event) => {
            if (event.target === event.currentTarget) setEnded(true);
          }}
        >
          <CheckIcon
            strokeWidth={3.5}
            className="size-4 animate-progress-step-check motion-reduce:[animation-duration:1ms]"
          />
        </div>
      </div>
    </ProgressStep>
  );
}

function ProgressSteps({
  totalSteps,
  currentStep,
  celebrateLastStep = true,
  className,
  ...props
}: ProgressStepsProps) {
  const celebratedIndex = celebrateLastStep ? currentStep - 2 : -1;

  const stateFor = (index: number): ProgressStepState => {
    if (index === currentStep - 1) return 'current';
    if (index < currentStep - 1) return 'complete';
    return 'pending';
  };

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={totalSteps}
      aria-valuenow={currentStep}
      aria-label={`Step ${currentStep} of ${totalSteps}`}
      data-slot="progress-steps"
      className={cn(
        'flex h-1 w-full min-w-0 gap-2 overflow-visible',
        className
      )}
      {...props}
    >
      {Array.from({ length: totalSteps }, (_, index) =>
        index === celebratedIndex ? (
          <CelebratingStep key={`celebrate-${currentStep}`} />
        ) : (
          <ProgressStep key={index} state={stateFor(index)} />
        )
      )}
    </div>
  );
}

export { ProgressSteps, progressStepVariants, type ProgressStepsProps };
